import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FILTER_TRIGGER_CLASS } from '../constants/filterBar';

const DEFAULT_MENU_MIN_WIDTH = 200;
const MENU_MAX_HEIGHT = 320;

function optionText(opt) {
  const raw = opt?.label ?? opt?.value ?? '';
  return raw == null ? '' : String(raw);
}

/**
 * Multi-select quick filter: button + portaled checkbox menu.
 * Stays open while toggling; auto-flips left/right and above/below.
 */
export default function MultiSelectFilter({
  options = [],
  values = [],
  onChange,
  emptyLabel = 'All',
  searchable = true,
  compact = true,
  className = '',
  menuMinWidth = DEFAULT_MENU_MIN_WIDTH,
  menuZIndex = 90,
  triggerClassName = '',
  menuClassName = ''
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: menuMinWidth });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const selectedSet = useMemo(() => new Set(values), [values]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...options].sort((a, b) =>
      optionText(a).localeCompare(optionText(b), undefined, { sensitivity: 'base' })
    );
    if (!q) return list;
    return list.filter(o => optionText(o).toLowerCase().includes(q));
  }, [options, query]);

  const buttonLabel = useMemo(() => {
    if (!values.length) return emptyLabel;
    if (values.length === 1) {
      const opt = options.find(o => o.value === values[0]);
      return opt?.label || values[0];
    }
    return `${values.length} selected`;
  }, [values, options, emptyLabel]);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuEl = menuRef.current;
    const menuWidth = Math.max(menuMinWidth, rect.width, menuEl?.offsetWidth || 0);
    const menuHeight = Math.min(menuEl?.offsetHeight || MENU_MAX_HEIGHT, MENU_MAX_HEIGHT);
    const gap = 4;
    const pad = 8;
    const vw = window.innerWidth;

    let left = rect.left;
    if (left + menuWidth > vw - pad) {
      left = Math.max(pad, rect.right - menuWidth);
    }
    if (left < pad) left = pad;

    let top = rect.bottom + gap;
    if (top + menuHeight > window.innerHeight - pad) {
      const above = rect.top - menuHeight - gap;
      if (above >= pad) top = above;
    }

    setPos({ top, left, width: menuWidth });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const raf = requestAnimationFrame(updatePosition);

    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    const onKey = e => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };
    const onPointerDown = e => {
      if (
        !triggerRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) {
        setOpen(false);
        setQuery('');
      }
    };

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, updatePosition, filteredOptions.length, values.length, query]);

  const toggleValue = val => {
    if (selectedSet.has(val)) {
      onChange(values.filter(v => v !== val));
    } else {
      onChange([...values, val]);
    }
  };

  const selectAllVisible = () => {
    const visibleValues = filteredOptions.map(o => o.value);
    onChange([...new Set([...values, ...visibleValues])]);
  };

  const clearAll = e => {
    e?.stopPropagation?.();
    onChange([]);
    setQuery('');
  };

  const hasSelection = values.length > 0;

  const menu =
    open &&
    createPortal(
      <div
        ref={menuRef}
        role="listbox"
        aria-multiselectable="true"
        className={`fixed border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-xl text-sm rounded-xl ${menuClassName}`}
        style={{
          top: pos.top,
          left: pos.left,
          width: pos.width,
          maxHeight: MENU_MAX_HEIGHT,
          zIndex: menuZIndex
        }}
        onMouseDown={e => e.preventDefault()}
      >
        {searchable && (
          <div className="p-2 border-b border-[hsl(var(--ds-border))] sticky top-0 bg-[hsl(var(--ds-surface))] z-10">
            <input
              type="text"
              className="border border-[hsl(var(--ds-border))] rounded-lg px-2 py-1.5 text-sm w-full focus:border-[hsl(var(--ds-primary))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ds-ring))]"
              placeholder="Search…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
            />
          </div>
        )}
        <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 border-b border-[hsl(var(--ds-border))] text-xs bg-[hsl(var(--ds-muted))]">
          <button type="button" className="text-[hsl(var(--ds-primary))] hover:underline" onClick={selectAllVisible}>
            Select visible
          </button>
          <button type="button" className="text-[hsl(var(--ds-muted-fg))] hover:underline" onClick={() => clearAll()}>
            Clear all
          </button>
        </div>
        <ul className="max-h-52 overflow-y-auto py-1">
          {filteredOptions.length === 0 ? (
            <li className="px-3 py-2 text-[hsl(var(--ds-muted-fg))] text-xs">No options match</li>
          ) : (
            filteredOptions.map(opt => {
              const checked = selectedSet.has(opt.value);
              return (
                <li key={opt.value}>
                  <label className="flex items-center gap-2.5 px-3 py-2 hover:bg-[hsl(var(--ds-muted))] cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="rounded border-[hsl(var(--ds-border))] text-[hsl(var(--ds-primary))] focus:ring-[hsl(var(--ds-primary))] shrink-0 h-4 w-4"
                      checked={checked}
                      onChange={() => toggleValue(opt.value)}
                    />
                    <span
                      className={`${compact ? 'truncate' : ''} ${checked ? 'font-medium text-[hsl(var(--ds-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}
                    >
                      {opt.label || opt.value}
                    </span>
                  </label>
                </li>
              );
            })
          )}
        </ul>
      </div>,
      document.body
    );

  return (
    <div className={`relative min-w-0 w-full h-9 flex items-center ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        className={`${FILTER_TRIGGER_CLASS} flex items-center ${
          hasSelection
            ? 'border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.06)]'
            : 'border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] bg-[hsl(var(--ds-surface))]'
        } ${open ? 'border-[hsl(var(--ds-primary))] ring-1 ring-[hsl(var(--ds-ring))]' : ''} ${triggerClassName}`}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="block truncate flex-1 min-w-0 pr-1">{buttonLabel}</span>
        {hasSelection && (
          <span
            role="button"
            tabIndex={0}
            className="absolute inset-y-0 right-7 flex items-center px-1 text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))] z-10"
            title="Clear filter"
            onClick={clearAll}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                clearAll(e);
              }
            }}
          >
            ✕
          </span>
        )}
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none text-[hsl(var(--ds-muted-fg))]">
          <svg
            className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {menu}
    </div>
  );
}
